import React, { useEffect } from 'react';
import { IconButton, Stack, Tooltip, Divider } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { undo, redo } from './actions/undoRedoActions';

/**
 * UndoRedoButtons - כפתורי Undo/Redo
 * Business Component - סטנד-אלון, מטפל בעצמו ב-Redux
 * 
 * הערה: ב-RTL (עברית), החיצים מתהפכים:
 * - Undo: חץ ימינה (RedoIcon) - חזרה לעבר
 * - Redo: חץ שמאלה (UndoIcon) - התקדמות קדימה
 * 
 * @param {function} dispatch - Redux dispatch
 * @param {number} historyIndex - מיקום נוכחי בהיסטוריה
 * @param {number} historyLength - אורך ההיסטוריה
 */
const UndoRedoButtons = ({ dispatch, historyIndex, historyLength }) => {
    const canUndo = historyIndex > 0 && historyLength >= 2;
    const canRedo = historyIndex < historyLength - 1;

    const handleUndo = () => {
        if (canUndo && dispatch) {
            dispatch(undo());
        }
    };

    const handleRedo = () => {
        if (canRedo && dispatch) {
            dispatch(redo());
        }
    };

    // Keyboard shortcuts for Undo/Redo
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ctrl+Z - Undo
            if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                if (canUndo && dispatch) {
                    dispatch(undo());
                }
            }

            // Ctrl+Y או Ctrl+Shift+Z - Redo
            if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'z')) {
                e.preventDefault();
                if (canRedo && dispatch) {
                    dispatch(redo());
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dispatch, canUndo, canRedo]);

    return (
        <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(30,58,95,0.1)' }} />
            <Stack direction="row" spacing={0.5} alignItems="center">
                {/* Undo - חץ ימינה (חזרה לעבר) */}
                <Tooltip title={canUndo ? `ביטול (Ctrl+Z) - ${historyIndex}/${historyLength - 1}` : 'אין מה לבטל'}>
                    <span>
                        <IconButton
                            onClick={handleUndo}
                            disabled={!canUndo}
                            size="small"
                            sx={{
                                color: canUndo ? '#1e3a5f' : '#cbd5e1',
                                '&:hover': {
                                    bgcolor: canUndo ? 'rgba(30,58,95,0.07)' : 'transparent'
                                },
                                '&:disabled': {
                                    color: '#cbd5e1'
                                }
                            }}
                        >
                            <RedoIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>

                {/* Redo - חץ שמאלה (התקדמות קדימה) */}
                <Tooltip title={canRedo ? `שחזור (Ctrl+Y) - ${historyIndex + 1}/${historyLength - 1}` : 'אין מה לשחזר'}>
                    <span>
                        <IconButton
                            onClick={handleRedo}
                            disabled={!canRedo}
                            size="small"
                            sx={{
                                color: canRedo ? '#1e3a5f' : '#cbd5e1',
                                '&:hover': {
                                    bgcolor: canRedo ? 'rgba(30,58,95,0.07)' : 'transparent'
                                },
                                '&:disabled': {
                                    color: '#cbd5e1'
                                }
                            }}
                        >
                            <UndoIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(30,58,95,0.1)' }} />
        </>
    );
};

export default UndoRedoButtons;
